import React from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../../components/ui/accordion";
import { useConsultation } from "../../../context/ConsultationContext";
import { ArrowRight } from "lucide-react";

const FAQ = [
  {
    q: "Why choose Dubai for starting a business?",
    a: "Dubai is a strategic global business destination offering world-class infrastructure, tax-friendly policies, political stability and a diverse, innovative economy. Its vibrant environment, ease of business setup and excellent growth opportunities make Dubai highly attractive for entrepreneurs and investors globally.",
  },
  {
    q: "What support does Dubai offer entrepreneurs starting a business?",
    a: "Dubai offers extensive support through simplified business-setup processes, competitive licensing, investor visas and dedicated free zones with tax exemptions and other incentives. Networking opportunities and government-backed initiatives help businesses grow from inception to expansion.",
  },
  {
    q: "What are my options for company formation in the UAE, and how much does it cost?",
    a: "You can establish your business in a Freezone, Mainland or Offshore jurisdiction — each with unique benefits. Freezones offer 100% foreign ownership and tax advantages, Mainland companies give access to the local UAE market, and Offshore entities suit international trade without a physical presence. Cost depends on jurisdiction, activity and license type. StartINC provides transparent pricing and tailored solutions.",
  },
  {
    q: "What kinds of business licenses are available in Dubai?",
    a: "Dubai offers Commercial, Professional, Industrial and Tourism licenses. Each caters to specific activities — trading, consultancy, manufacturing, hospitality — and StartINC helps businesses pick the right license based on industry and operational needs.",
  },
  {
    q: "How long does company formation take, and what documents are required?",
    a: "Company formation typically takes three weeks to a few months depending on business type, jurisdiction and approvals. Documents include shareholder passport copies, visa copies (if applicable), proof of address, a business plan or profile and activity-specific approvals. StartINC provides a detailed checklist tailored to your setup.",
  },
  {
    q: "How does StartINC support accounting and auditing in the UAE?",
    a: "We provide tailored accounting and auditing services ensuring compliance with UAE regulations. Our team manages financial records, VAT obligations and audits while aligning practices with international standards — enhancing financial transparency and streamlining compliance for sustained growth.",
  },
  {
    q: "Why should I outsource my bookkeeping and accounting?",
    a: "Outsourcing simplifies workload, saves time and ensures expert management of your finances. StartINC handles these tasks efficiently, letting you focus on business growth instead of back-office operations.",
  },
  {
    q: "What makes StartINC a reliable partner for setup and accounting?",
    a: "Our global experts have extensive experience, deep industry knowledge and a proven track record navigating UAE regulations. We offer efficient, compliant and personalised solutions — backed by SLAs, transparent pricing and a single point of accountability.",
  },
];

export default function FAQV2({ items, eyebrow = "Frequently asked", title }) {
  const { open } = useConsultation();
  const list = items || FAQ;

  return (
    <section
      data-testid="v2-faq"
      className="relative bg-[#F8FAFC] py-10 md:py-14"
    >
      <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <div className="sticky top-28">
            <p className="overline text-[#3366FF]">{eyebrow}</p>
            <div className="gold-rule mt-5 mb-6" />
            <h2 className="font-heading font-light tracking-tight text-[#0F172A] text-2xl sm:text-3xl leading-tight">
              {title || (
                <>
                  Everything founders ask{" "}
                  <span className="font-black text-[#002366]">before they sign</span>.
                </>
              )}
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              Didn't find your answer? Book a free 20-minute call with a senior
              StartINC advisor — we'll give you a straight response in writing.
            </p>
            <button
              data-testid="v2-faq-cta"
              onClick={() => open("other")}
              className="btn-gold mt-6"
            >
              Get In Touch <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="lg:col-span-8">
          <Accordion
            type="single"
            collapsible
            className="space-y-3"
            data-testid="v2-faq-accordion"
          >
            {list.map((item, i) => (
              <AccordionItem
                key={item.q}
                value={`v2-faq-${i}`}
                data-testid={`v2-faq-item-${i}`}
                className="rounded-sm border border-slate-200 bg-white px-5 md:px-7 data-[state=open]:border-[#3366FF]/40 data-[state=open]:shadow-md"
              >
                <AccordionTrigger className="font-heading font-semibold text-[#0F172A] text-left text-base md:text-lg tracking-tight py-5 hover:no-underline">
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 leading-relaxed text-sm md:text-base pb-6">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}
